import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { mediaUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { LocationSelect } from "../components/LocationSelect";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Camera, ArrowLeft, Briefcase } from "lucide-react";

export default function EditProfile() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [meta, setMeta] = useState({ sectors: [] });
  const [form, setForm] = useState({ display_name: "", trade: [], city: "", bio: "", years_experience: "" });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/profile/me").then((r) => {
      const p = r.data;
      setForm({
        display_name: p.display_name || user.name || "",
        trade: p.trade || [],
        city: p.city || "",
        bio: p.bio || "",
        years_experience: p.years_experience || "",
      });
      setPhotoUrl(p.photo_url || null);
    });
  }, [user]);

  if (loading || !user) return null;

  const toggleTrade = (id) => {
    setForm((prev) => ({
      ...prev,
      trade: prev.trade.includes(id) ? prev.trade.filter((x) => x !== id) : [...prev.trade, id],
    }));
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(r.data.photo_url);
      toast.success(lang === "fr" ? "Photo mise à jour" : "Photo updated");
    } catch {
      toast.error("Erreur");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.display_name.trim()) { toast.error(lang === "fr" ? "Nom requis" : "Name required"); return; }
    setSaving(true);
    try {
      await api.put("/profile", {
        ...form,
        years_experience: form.years_experience ? parseInt(form.years_experience) : null,
      });
      toast.success(lang === "fr" ? "Profil enregistré" : "Profile saved");
      navigate(`/pros/${user.user_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <button onClick={() => navigate("/dashboard")} data-testid="back-to-dash" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
        <ArrowLeft className="h-4 w-4" /> {t("nav_dashboard")}
      </button>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">{t("profile_title")}</h1>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm space-y-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            <Avatar className="h-20 w-20">
              <AvatarImage src={mediaUrl(photoUrl)} alt="me" />
              <AvatarFallback className="bg-primary/10 text-primary text-xl font-bold">{form.display_name?.slice(0, 2).toUpperCase() || "?"}</AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="upload-photo-btn"
              className="absolute -bottom-1 -right-1 grid place-items-center h-8 w-8 rounded-full bg-primary text-primary-foreground shadow-md hover:bg-primary/90"
            >
              <Camera className="h-4 w-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="photo-input" />
          </div>
          <div>
            <p className="font-medium">{t("photo_label")}</p>
            <p className="text-sm text-muted-foreground">{uploading ? t("processing") : (lang === "fr" ? "JPG, PNG ou WEBP" : "JPG, PNG or WEBP")}</p>
          </div>
        </div>

        <div>
          <Label htmlFor="dn">{t("display_name")}</Label>
          <Input id="dn" data-testid="profile-name-input" className="mt-1.5" value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        </div>

        <div>
          <Label>{t("trade_label")}</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {meta.sectors.map((s) => (
              <button key={s.id} type="button" data-testid={`trade-${s.id}`} onClick={() => toggleTrade(s.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-all ${
                  form.trade.includes(s.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                }`}>
                <Briefcase className="h-3.5 w-3.5" />{lang === "fr" ? s.fr : s.en}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>{t("city_label")}</Label>
            <LocationSelect groups={meta.location_groups || []} value={form.city}
              onChange={(v) => setForm({ ...form, city: v })} testId="profile-city-select"
              className="mt-1.5" placeholder="—" />
          </div>
          <div>
            <Label htmlFor="ye">{t("years_label")}</Label>
            <Input id="ye" type="number" min="0" data-testid="profile-years-input" className="mt-1.5" value={form.years_experience}
              onChange={(e) => setForm({ ...form, years_experience: e.target.value })} />
          </div>
        </div>

        <div>
          <Label htmlFor="bio">{t("bio_label")}</Label>
          <Textarea id="bio" rows={4} data-testid="profile-bio-input" className="mt-1.5" value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })} />
        </div>

        <Button className="w-full" onClick={save} disabled={saving} data-testid="save-profile-btn" size="lg">
          {saving ? t("processing") : t("save_profile")}
        </Button>
      </div>
    </div>
  );
}
